import { useCallback, useMemo, useState } from 'react';
import { getCurrentUser } from 'aws-amplify/auth';
import AcademicApplicationService from '@/services/AcademicApplicationService';
import { refreshSession } from '@/services/RefreshSession';
import { useAuthStore } from '@/store/authStore';
import { enrichUserWithCognitoAttributes } from '@/utils/enrichUserWithCognitoAttributes';
import { getTrialDaysRemaining, isTrialExpired, mergeTrialAttributes } from '@/utils/trialUtils';

const hasUsedTrialRevert = (user: Record<string, unknown> | null | undefined) =>
  String(user?.['custom:trial_reverted'] ?? '') === '1';

export function useRevertAcademicToTrial() {
  const user = useAuthStore(state => state.user);
  const groups = useAuthStore(state => state.groups);
  const trialAttributes = useAuthStore(state => state.trialAttributes);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isAcademic = groups?.includes('ROLE_ACADEMIC') ?? false;
  const alreadyReverted = hasUsedTrialRevert(user);

  const trialUser = useMemo(
    () => mergeTrialAttributes(user, trialAttributes ?? undefined),
    [user, trialAttributes],
  );

  const trialDaysRemaining = useMemo(() => {
    if (!isAcademic || !trialUser) return null;
    if (isTrialExpired(trialUser)) return null;
    const days = getTrialDaysRemaining(trialUser);
    return days > 0 ? days : null;
  }, [isAcademic, trialUser]);

  const canRevert = Boolean(isAcademic && trialDaysRemaining && user?.email && !alreadyReverted);

  const revertToTrial = useCallback(async () => {
    if (!user?.email || !canRevert) {
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await AcademicApplicationService.revertAcademicToTrial(user.email);
      if (response.status === 'error') {
        setError(response.message);
        throw new Error(response.message);
      }

      sessionStorage.removeItem('showAcademicUpgradeModal');

      // Cognito group changes can lag briefly in new tokens (same pattern as payment success).
      await new Promise(resolve => setTimeout(resolve, 1500));

      const session = await refreshSession({ forceRefresh: true });
      if (!session?.tokens?.idToken?.payload) {
        throw new Error('Could not refresh session after restoring trial');
      }

      const userInfo = session.tokens.idToken.payload as Record<string, unknown>;
      const userGroups = (userInfo['cognito:groups'] as string[]) || [];
      const currentUser = await getCurrentUser();
      const enriched = await enrichUserWithCognitoAttributes({
        ...userInfo,
        ...currentUser,
      });

      useAuthStore.getState().setUser(enriched);
      useAuthStore.getState().setGroups(userGroups);
      await useAuthStore.getState().fetchUserType();

      window.location.reload();
    } catch (err) {
      setLoading(false);
      if (err instanceof Error) {
        setError(err.message);
      }
      throw err;
    }
  }, [user?.email, canRevert]);

  return {
    canRevert,
    alreadyReverted,
    trialDaysRemaining,
    revertToTrial,
    loading,
    error,
  };
}
